import axios from 'axios';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
    ActionTypes,
    AutomatedRuleRecord,
    FetchRulesRequestAction,
    ToggleStatusRequestAction,
    UpdateRuleRequestAction,
    fetchRulesFailure,
    fetchRulesSuccess,
    toggleStatusFailure,
    toggleStatusSuccess,
    updateRuleFailure,
    updateRuleSuccess,
} from './actions';

// ── API helpers ───────────────────────────────────────────────────────────────

const api = {
  fetchRules: async (page: number, size: number, audience?: string, status?: string) => {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    if (audience) params.set('audience', audience);
    if (status)   params.set('status', status);
    const res = await axios.get(`/api/automation-rules?${params.toString()}`);
    return res.data as { content: AutomatedRuleRecord[]; totalElements: number };
  },

  updateRule: async (id: string, updates: Partial<AutomatedRuleRecord>) => {
    const res = await axios.put(`/api/automation-rules/${id}`, updates);
    return res.data as AutomatedRuleRecord;
  },

  pauseRule: async (id: string) => {
    const res = await axios.post(`/api/automation-rules/${id}/pause`);
    return res.data as AutomatedRuleRecord;
  },

  resumeRule: async (id: string) => {
    const res = await axios.post(`/api/automation-rules/${id}/resume`);
    return res.data as AutomatedRuleRecord;
  },
};

// ── Workers ───────────────────────────────────────────────────────────────────

function* fetchRulesSaga(action: FetchRulesRequestAction) {
  try {
    const { page, size, audience, status } = action.payload;
    const data: { content: AutomatedRuleRecord[]; totalElements: number } =
      yield call(api.fetchRules, page, size, audience, status);
    yield put(fetchRulesSuccess(data.content, data.totalElements));
  } catch (error: any) {
    console.error('[AutomatedEmailsSaga] fetchRules error:', error);
    yield put(fetchRulesFailure('Failed to load automation rules'));
  }
}

function* updateRuleSaga(action: UpdateRuleRequestAction) {
  try {
    const { id, updates } = action.payload;
    const rule: AutomatedRuleRecord = yield call(api.updateRule, id, updates);
    yield put(updateRuleSuccess(rule));
  } catch (error: any) {
    console.error('[AutomatedEmailsSaga] updateRule error:', error);
    yield put(updateRuleFailure('Failed to save rule changes'));
  }
}

function* toggleStatusSaga(action: ToggleStatusRequestAction) {
  try {
    const { id, currentStatus } = action.payload;
    const rule: AutomatedRuleRecord =
      currentStatus === 'ACTIVE'
        ? yield call(api.pauseRule, id)
        : yield call(api.resumeRule, id);
    yield put(toggleStatusSuccess(rule));
  } catch (error: any) {
    console.error('[AutomatedEmailsSaga] toggleStatus error:', error);
    yield put(toggleStatusFailure('Failed to update rule status'));
  }
}

// ── Watcher ───────────────────────────────────────────────────────────────────

export function* automatedEmailsSaga() {
  yield takeLatest(ActionTypes.FETCH_RULES_REQUEST, fetchRulesSaga);
  yield takeLatest(ActionTypes.UPDATE_RULE_REQUEST, updateRuleSaga);
  yield takeLatest(ActionTypes.TOGGLE_STATUS_REQUEST, toggleStatusSaga);
}
